import * as _ from 'lodash-es';
import Quill, { Parchment } from 'quill/core';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { AttachmentFile } from './Attachment';
import * as styles from './AttachmentMention.module.scss';
import Comment from './Comment';
import Diff from './Diff';
import * as mentionStyles from './Mention.module.scss';
import ReactEmbed from './ReactEmbed';
import Reaction from './Reaction';
import { convertAttachmentsToHtmlList } from './utils/attachmentsHtml';
import VersionChange from './VersionChange';
import ContentPreviewPopOver from '../../components/ContentPreviewPopOver';
import fileIcon from '../../icons/non-prefilled/16x16/file.svg';
import { ENTITY_CARD_HOVER_GROUP_ID } from '../../post/constants';
import AttachmentUploader, {
  filterValidFiles,
  isAttachmentUploaded,
} from '../../utils/AttachmentUploader';
import { useIsSize } from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import { getAssetTokenFromBlot } from '../AssetTokenContext';
import { AttachmentGrid } from '../attachment/AttachmentContainer';
import AttachmentItem from '../attachment/AttachmentItem';
import { useDownloadAttachments } from '../attachment/DownloadAttachmentsProvider';
import MentionBase, { MENTION_CONTAINER_CLASS } from '../mentions/MentionBase';
import { useQuill } from '../QuillContext';
import type { RenderPortalArgs } from '../QuillEditor';
import findQuillByBlot from '../utils/findQuillByBlot';

export function getDefaultAttachmentMentionText(attachments: AttachmentFile[]) {
  return `${attachments.at(0)?.name ?? 'Unknown'}${
    attachments.length > 1 ? ` + ${attachments.length - 1} more` : ''
  }`;
}

interface BaseAttachmentMentionProps {
  mentionText?: string | null;
  visibleAttachments: AttachmentFile[];
  innerHeadRef?: React.Ref<HTMLSpanElement>;
  selected?: boolean;
}

export const BaseAttachmentMention = ({
  mentionText,
  visibleAttachments,
  innerHeadRef,
  selected,
}: BaseAttachmentMentionProps) => {
  const content =
    mentionText ?? getDefaultAttachmentMentionText(visibleAttachments);

  return (
    <MentionBase
      icon={fileIcon}
      type="attachment"
      content={content}
      innerHeadRef={innerHeadRef}
      selected={selected}
    />
  );
};

const MENTION_TEXT_ATTRIBUTE = 'data-blot-mention-text';

interface AttachmentMentionPopOverProps {
  visibleAttachments: AttachmentFile[];
  onDeleteAttachment?: (source: string) => void;
  children: React.ReactNode;
  reference: React.RefObject<HTMLElement>;
  blot: AttachmentMention;
}

const AttachmentMentionPopOver = ({
  visibleAttachments,
  onDeleteAttachment,
  children,
  reference,
  blot,
}: AttachmentMentionPopOverProps) => {
  const [popOverFloatingElement, setPopOverFloatingElement] =
    useState<HTMLDivElement | null>(null);
  const popOverRef = useRef<HTMLDivElement>(null);
  const downloadAttachments = useDownloadAttachments();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { quill, readOnly } = useQuill();
  const isMobile = useIsSize(Sizes.SM);
  const [attachmentWithOpenMenu, setAttachmentWithOpenMenu] =
    useState<AttachmentFile | null>(null);
  const shouldShowPopOver = isPreviewOpen || attachmentWithOpenMenu != null;

  const downloadableAttachments =
    visibleAttachments.filter(isAttachmentUploaded);
  const handleDownloadAll =
    downloadAttachments && downloadableAttachments.length > 0
      ? () => downloadAttachments(downloadableAttachments)
      : null;

  const minWidth = useMemo(
    () => (shouldShowPopOver ? quill?.root.offsetWidth : undefined),
    [quill, shouldShowPopOver],
  );

  const [minHeight, setMinHeight] = useState(0);

  useLayoutEffect(() => {
    setMinHeight(popOverRef.current?.offsetHeight ?? 0);
  }, [shouldShowPopOver, visibleAttachments.length]);

  return (
    <ContentPreviewPopOver
      minWidth={minWidth}
      minHeight={minHeight}
      display="inline"
      open={shouldShowPopOver}
      onOpenChange={setIsPreviewOpen}
      anchorRef={reference}
      hoverGroupId={ENTITY_CARD_HOVER_GROUP_ID}
      floatingRef={setPopOverFloatingElement}
      popOver={
        <div ref={popOverRef} className={styles.popOverContainer}>
          <AttachmentGrid count={visibleAttachments.length} narrow={isMobile}>
            {visibleAttachments.map((attachment) => (
              <AttachmentItem
                key={attachment.source}
                blot={blot}
                attachment={attachment}
                onOverflowMenuOpenChange={(isOpen) => {
                  setAttachmentWithOpenMenu((current) => {
                    if (current === attachment) return null;
                    return isOpen ? attachment : current;
                  });
                }}
                isOverflowMenuOpen={attachmentWithOpenMenu === attachment}
                readOnly={readOnly}
                isAttachmentUploaded={isAttachmentUploaded}
                onDeleteAttachment={
                  onDeleteAttachment &&
                  (() => onDeleteAttachment(attachment.source))
                }
                onDownloadAll={handleDownloadAll}
                overflowMenuPortal={popOverFloatingElement ?? undefined}
              />
            ))}
          </AttachmentGrid>
        </div>
      }
      placement="bottom-start"
    >
      {children}
    </ContentPreviewPopOver>
  );
};

type AttachmentMentionState = {
  value: AttachmentFile[];
  mentionText: string | null;
};

class AttachmentMention extends ReactEmbed<AttachmentMentionState> {
  static blotName = 'attachment-mention';
  static className = 'ql-attachment-mention';
  static tagName = 'SPAN';

  static create(value: AttachmentFile[]) {
    const node = super.create(value) as HTMLSpanElement;
    node.classList.add(mentionStyles.container);
    return node;
  }

  static formats(domNode: Element) {
    const attributes: {
      'mention-text'?: string | null;
    } = {};

    if (domNode.hasAttribute(MENTION_TEXT_ATTRIBUTE)) {
      attributes['mention-text'] = domNode.getAttribute(MENTION_TEXT_ATTRIBUTE);
    }
    return _.isEmpty(attributes) ? null : attributes;
  }

  attributes: Parchment.AttributorStore;

  constructor(
    scroll: Parchment.ScrollBlot,
    domNode: HTMLElement,
    value: AttachmentFile[],
  ) {
    const mentionText =
      AttachmentMention.formats(domNode)?.['mention-text'] ?? null;
    super(scroll, domNode, { value, mentionText });
    this.contentNode.classList.add(mentionStyles.content);
    this.contentNode.classList.add(styles.content);

    this.appendAttachment = this.appendAttachment.bind(this);
    this.handleDeleteAttachment = this.handleDeleteAttachment.bind(this);
    this.attributes = new Parchment.AttributorStore(domNode);
  }

  attach() {
    super.attach();
    AttachmentUploader.addActiveAttachmentBlot(this);
  }

  detach() {
    super.detach();
    AttachmentUploader.removeActiveAttachmentBlot(this);
  }

  setMentionText(mentionText: string | null) {
    this.setState({ mentionText });
    if (this.domNode instanceof HTMLElement) {
      if (mentionText == null) {
        this.domNode.removeAttribute(MENTION_TEXT_ATTRIBUTE);
      } else {
        this.domNode.setAttribute(MENTION_TEXT_ATTRIBUTE, mentionText);
      }
    }
  }

  format(name: string, value: unknown) {
    if (
      name === 'mention-text' &&
      (typeof value === 'string' || value == null)
    ) {
      this.setMentionText(value ?? null);
    } else if (
      (name === Diff.attrName || name === VersionChange.attrName) &&
      this.domNode instanceof HTMLElement &&
      this.domNode.getAttribute(`data-${name}`) !== value
    ) {
      if (value == null) {
        this.domNode.removeAttribute(`data-${name}`);
      } else if (typeof value === 'string') {
        this.domNode.setAttribute(`data-${name}`, value);
      }
    } else if (name === Comment.attrName || name === Reaction.attrName) {
      const attributor = this.scroll.query(name, Parchment.Scope.ATTRIBUTE);
      if (attributor instanceof Parchment.Attributor) {
        this.attributes.attribute(attributor, value);
      }
    } else {
      super.format(name, value);
    }
  }

  formatAt(index: number, length: number, name: string, value: unknown) {
    if (
      [
        Comment.attrName,
        Reaction.attrName,
        'mention-text',
        Diff.attrName,
        VersionChange.attrName,
      ].includes(name)
    ) {
      super.formatAt(index, length, name, value);
    }
  }

  getReferenceNodeForBounds() {
    return (
      this.contentNode.querySelector(`.${MENTION_CONTAINER_CLASS}`) ??
      this.contentNode
    );
  }

  formats() {
    return {
      ...this.attributes.values(),
      ...this.statics.formats(this.domNode),
    };
  }

  html() {
    const assetToken = getAssetTokenFromBlot(this);
    const attachments = this.getAttachmentFiles().filter(isAttachmentUploaded);
    const attachmentsHtmlList = convertAttachmentsToHtmlList(
      attachments,
      assetToken,
    );
    return `<div class="${AttachmentMention.className}">${attachmentsHtmlList}</div>`;
  }

  cleanUpload(uploadFileId: string, source: string | null | undefined) {
    const attachmentFiles = this.getAttachmentFiles().slice();
    const targetIndex = attachmentFiles.findIndex(
      (item) => item.source === uploadFileId,
    );
    if (targetIndex !== -1) {
      if (source) {
        const current = attachmentFiles[targetIndex];
        if (current) {
          current.source = source;
        }
      } else {
        attachmentFiles.splice(targetIndex, 1);
      }
    }
    this.setValue(attachmentFiles);
    this.setState({ value: attachmentFiles });
  }

  appendAttachment(files: File[]) {
    if (!files.length) return;

    const index = this.offset(this.scroll);
    if (index >= 0) {
      findQuillByBlot(this)?.setSelection(
        index,
        this.length(),
        Quill.sources.USER,
      );
    }

    const validFiles = filterValidFiles(files);
    if (validFiles.length) {
      AttachmentUploader.uploadFiles(validFiles, this);

      const newValue = this.store
        .getState()
        .value.concat(
          AttachmentUploader.normalizeToAttachmentFiles(validFiles),
        );
      this.setValue(newValue);
      this.setState({ value: newValue });
    }
  }

  getAttachmentFiles(): AttachmentFile[] {
    return this.store.getState().value;
  }

  getMentionText(): string | null {
    return this.store.getState().mentionText;
  }

  getVisibleAttachments() {
    return this.getAttachmentFiles().filter(
      (attachment) =>
        isAttachmentUploaded(attachment) ||
        AttachmentUploader.shouldShowUploadingAttachment(attachment),
    );
  }

  handleDeleteAttachment(source: string) {
    const attachmentFiles = this.getAttachmentFiles();
    const index = this.getAttachmentFiles().findIndex(
      (item) => item.source === source,
    );
    if (index !== -1) {
      attachmentFiles.splice(index, 1);

      const hasVisibleAttachments = attachmentFiles.some(
        (attachment) =>
          isAttachmentUploaded(attachment) ||
          AttachmentUploader.shouldShowUploadingAttachment(attachment),
      );
      if (hasVisibleAttachments) {
        this.setValue(attachmentFiles);
        this.setState({ value: attachmentFiles });
      } else {
        this.remove();
      }
    }
  }

  render(
    { mentionText }: AttachmentMentionState,
    { selected }: RenderPortalArgs,
  ) {
    const visibleAttachments = this.getVisibleAttachments();
    const innerHeadRef = React.createRef<HTMLSpanElement>();

    // Other collaborators may be uploading
    if (visibleAttachments.length === 0) {
      return null;
    }

    return (
      <AttachmentMentionPopOver
        visibleAttachments={visibleAttachments}
        onDeleteAttachment={this.handleDeleteAttachment}
        reference={innerHeadRef}
        blot={this}
      >
        <BaseAttachmentMention
          mentionText={mentionText}
          visibleAttachments={visibleAttachments}
          innerHeadRef={innerHeadRef}
          selected={selected}
        />
      </AttachmentMentionPopOver>
    );
  }
}

export default AttachmentMention;
